// §5.4 Login — live OTP login; login-mode called 350ms after typing stops.
// Phase 5 login is always password-first, followed by OTP verification.
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldHalved,
  faWallet,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import {
  AuthApiError,
  loginWithPassword,
} from '@/api/auth';
import { getAccessToken } from '@/api/session';
import { AUTH_LABELS } from '@/constants/static/loginLabels';
import skyline from '../../assets/images/skyline.svg';
import '../Register/Register.css';

const LoginScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const loginState = (location.state as { notice?: string } | null) ?? {};
  const notice = loginState.notice ?? '';

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.state) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.state]);

  // Browser back can expose an earlier login history entry while the session
  // is still valid. Redirect during render so the login form never flashes.
  if (getAccessToken()) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogin = async () => {
    setError('');
    if (!identifier.trim()) {
      setError('Please enter your email, Customer ID or mobile number.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    setLoading(true);
    try {
      // Password login starts the mandatory OTP step; session tokens arrive after OTP verification.
      const otpSession = await loginWithPassword(identifier, password);
      navigate('/login-otp', {
        state: {
          identifier: identifier.trim(),
          transactionId: otpSession.transactionId,
          deliveryMethod: otpSession.deliveryMethod,
          maskedDestination: otpSession.maskedDestination,
          expiresInSeconds: otpSession.expiresInSeconds,
        },
      });
    } catch (err) {
      setError(
        err instanceof AuthApiError
          ? err.message
          : "We couldn't find an account for that email, Customer ID or mobile number."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="nova-register-page">
      <div className="nova-register-container">
        {/* Left Hero Section — same panel as Welcome / Login / OTP */}
        <div className="nova-hero-section">
          <div className="nova-hero-content">
            <div className="nova-logo">
              <div className="nova-logo-mark">{AUTH_LABELS.app.logoMark}</div>
              <span className="nova-logo-text">{AUTH_LABELS.app.name}</span>
            </div>

            <h1 className="nova-hero-title">
              {AUTH_LABELS.hero.titleLine1}
              <br />
              {AUTH_LABELS.hero.titleLine2}
            </h1>

            <div className="nova-feature-cards">
              <div className="nova-feature-card">
                <div className="nova-feature-icon">
                  <FontAwesomeIcon icon={faShieldHalved} />
                </div>
                <div className="nova-feature-body">
                  <h3 className="nova-feature-title">{AUTH_LABELS.hero.featureSecureTitle}</h3>
                  <p className="nova-feature-description">
                    {AUTH_LABELS.hero.featureSecureDescription}
                  </p>
                </div>
              </div>

              <div className="nova-feature-card">
                <div className="nova-feature-icon">
                  <FontAwesomeIcon icon={faWallet} />
                </div>
                <div className="nova-feature-body">
                  <h3 className="nova-feature-title">{AUTH_LABELS.hero.featureManageTitle}</h3>
                  <p className="nova-feature-description">
                    {AUTH_LABELS.hero.featureManageDescription}
                  </p>
                </div>
              </div>

              <div className="nova-feature-card">
                <div className="nova-feature-icon">
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <div className="nova-feature-body">
                  <h3 className="nova-feature-title">{AUTH_LABELS.hero.featureBankingTitle}</h3>
                  <p className="nova-feature-description">
                    {AUTH_LABELS.hero.featureBankingDescription}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <img src={skyline} alt={AUTH_LABELS.hero.skylineAlt} className="nova-skyline" />
        </div>

        {/* Right Section — login form on a white card */}
        <div className="nova-form-section">
          <div style={{ width: '100%', maxWidth: 600 }}>
            <div className="nova-form-card">
              <button
                type="button"
                aria-label="Back"
                onClick={() => navigate('/welcome')}
                style={{
                  width: 40,
                  height: 40,
                  border: 'none',
                  borderRadius: 10,
                  background: '#eef2f8',
                  color: '#122b4f',
                  fontSize: '1.1rem',
                  cursor: 'pointer',
                  marginBottom: 24,
                }}
              >
                ←
              </button>

              <div className="text-center mb-4">
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: 'var(--nova-primary)',
                    color: '#ffffff',
                    fontSize: '1.8rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px',
                  }}
                >
                  {AUTH_LABELS.app.logoMark}
                </div>
                <h1
                  style={{
                    fontSize: '1.4rem',
                    fontWeight: 700,
                    color: '#122b4f',
                    whiteSpace: 'pre-line',
                    marginBottom: 0,
                  }}
                >
                  {'Thank you for choosing\nNovaBank Personal Banking'}
                </h1>
              </div>

              <div className="form-floating mb-3 text-start">
                <input
                  id="identifier"
                  autoComplete="off"
                  className="form-control"
                  placeholder="Email, Customer ID or mobile"
                  value={identifier}
                  onChange={(e) => {
                    setIdentifier(e.target.value);
                    setError('');
                  }}
                />
                <label htmlFor="identifier">Email, Customer ID or mobile</label>
              </div>

              {notice && <div className="alert alert-success py-2 text-start" role="status">{notice}</div>}
              <>
                  <div className="form-floating mb-2 text-start position-relative">
                    <input
                      id="password"
                      autoComplete="new-password"
                      type={showPassword ? 'text' : 'password'}
                      className="form-control"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError('');
                      }}
                    />
                    <label htmlFor="password">Password</label>
                    <button
                      type="button"
                      className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                      style={{ color: 'var(--nova-primary)', textDecoration: 'none' }}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      style={{ fontSize: '0.85rem', color: 'var(--nova-primary)' }}
                      onClick={() => navigate('/forgot-username')}
                    >
                      Forgot username
                    </button>
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      style={{ fontSize: '0.85rem', color: 'var(--nova-primary)' }}
                      onClick={() => navigate('/forgot-password')}
                    >
                      Forgot password
                    </button>
                  </div>
              </>

              {error && (
                <div className="alert alert-danger py-2 text-start" role="alert">
                  {error}
                </div>
              )}

              <button
                type="button"
                disabled={loading}
                onClick={handleLogin}
                style={{
                  width: '100%',
                  padding: '14px 0',
                  background: 'var(--nova-primary)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: loading ? 0.8 : 1,
                }}
              >
                {loading && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
                Login
              </button>

              <button type="button" className="btn btn-link mt-3 w-100" onClick={() => navigate('/temporary-password')}>
                Temporary password expired or lost? Get a new one
              </button>
              <button type="button" className="btn btn-link w-100" onClick={() => navigate('/track')}>
                Track my application
              </button>
              <p className="mt-3 text-center" style={{ fontSize: '0.8rem', color: '#5c6b80' }}>
                NovaBank never asks for your OTP, PIN or password.
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default LoginScreen;
